import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../../../lib/api';
import { Plane, Clock, FileCheck, ArrowRight, CalendarClock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { formatINR } from '../../../lib/format';

const KPI = ({ icon: Icon, label, value, tone = 'default', testId }) => {
  const tones = { default: 'bg-white border-slate-200', amber: 'bg-amber-50 border-amber-200', green: 'bg-emerald-50 border-emerald-200' };
  return (
    <div className={`rounded-sm border p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-500"><Icon size={13} /> {label}</div>
      <div className="text-2xl font-display font-extrabold mt-2 text-[#3b4872]">{value}</div>
    </div>
  );
};

export default function TravelDashboard() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { apiGet('/hr/travel/dashboard').then(setD).catch(() => setD({})); }, []);
  if (!d) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="travel-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">HR · Admin</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Travel Dashboard</h1>
          <p className="text-sm text-slate-600 mt-1">Approvals · Upcoming travel · Expense settlement.</p>
        </div>
        <button onClick={() => nav('/app/hr/travel/requests')} data-testid="open-requests-btn" className="px-4 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-2"><Plane size={14} /> Requests</button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <KPI icon={Clock} label="Pending Approvals" value={d.pending_count || 0} tone={d.pending_count ? 'amber' : 'default'} testId="kpi-pending" />
        <KPI icon={CalendarClock} label="Upcoming (30d)" value={d.upcoming_count || 0} testId="kpi-upcoming" />
        <KPI icon={FileCheck} label="Expenses Awaiting" value={d.expenses_awaiting_count || 0} tone={d.expenses_awaiting_count ? 'amber' : 'default'} testId="kpi-expenses" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card data-testid="pending-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2"><Clock size={15} /> Pending Approvals</CardTitle></CardHeader>
          <CardContent>
            {(d.pending_approvals || []).length === 0 ? <div className="text-xs text-slate-400 py-4 text-center">Nothing pending.</div> : (
              <div className="divide-y divide-slate-100">
                {d.pending_approvals.map((r) => (
                  <button key={r.id} onClick={() => nav(`/app/hr/travel/requests/${r.id}`)} data-testid={`pending-${r.id}`} className="w-full text-left py-2 hover:bg-slate-50 px-2 -mx-2 rounded-sm flex items-center justify-between">
                    <div><div className="font-display font-bold text-xs">{r.employee_name}</div><div className="text-[11px] text-slate-500">{r.from_location} → {r.to_location} · {r.departure_date || 'TBD'}</div></div>
                    <ArrowRight size={14} className="text-slate-400" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="upcoming-travel-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2"><CalendarClock size={15} /> Upcoming Travel</CardTitle></CardHeader>
          <CardContent>
            {(d.upcoming_travel || []).length === 0 ? <div className="text-xs text-slate-400 py-4 text-center">No upcoming travel.</div> : (
              <div className="divide-y divide-slate-100">
                {d.upcoming_travel.map((r) => (
                  <button key={r.id} onClick={() => nav(`/app/hr/travel/requests/${r.id}`)} data-testid={`upcoming-${r.id}`} className="w-full text-left py-2 hover:bg-slate-50 px-2 -mx-2 rounded-sm">
                    <div className="font-display font-bold text-xs">{r.employee_name} · {r.from_location} → {r.to_location}</div>
                    <div className="text-[11px] text-slate-500">{r.departure_date} · {r.mode}{r.estimated_cost ? ` · ${formatINR(r.estimated_cost)}` : ''}</div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
